import Checkbox from '@jetbrains/ring-ui/components/checkbox/checkbox'
import plur from 'plur'
import {memo, Suspense, useMemo} from 'react'
import {shallowEqual} from 'react-redux'

import {useSyncedBooleanQueryParam} from '../../../../hooks/routes'
import {useProjectData} from '../../../../hooks/useEntity'
import {useThrottle} from '../../../../hooks/useThrottle'
import {getBuildsData} from '../../../../selectors'
import {restApi} from '../../../../services/rest'
import type {BuildId} from '../../../../types'
import {toBuildId} from '../../../../types'
import {UserProperties} from '../../../../utils/userProperties'
import {getSnapshotDependenciesLocator} from '../../../common/BuildInfoTable/SnapshotDependenciesCounters/SnapshotDependenciesCounters.utils'
import ContentPanel from '../../../common/ContentPanel/ContentPanel'
import contentPanelStyles from '../../../common/ContentPanel/ContentPanel.css'
import {useSequenceLoaderReadyHandler} from '../../../common/SequenceLoader/SequenceLoader.hooks'
import {TreeEntityType} from '../BuildProblems.types'
import BuildProblemsTree from '../BuildProblemsTree/BuildProblemsTree.container'
import {buildProblemsTreeContextDefaultValue} from '../BuildProblemsTree/BuildProblemsTree.context'

import {useBuildProblemsSubscriptions} from './BuildProblemsPreview.hooks'
import {
  getProblemOccurrencesLocator,
  getProblemRootCausesLocator,
  getRootNode,
} from './BuildProblemsPreview.utils'

import styles from './BuildProblemsPreview.css'

import {useAppSelector} from 'src/hooks/react-redux'

type Props = {
  buildId: BuildId
  seqKey?: string
}
const THROTTLER_TIMEOUT = 10000

function BuildProblemsPreview({buildId}: Props) {
  const projectId = useProjectData({buildId}, project => project?.id)

  const problemsLocator = getProblemOccurrencesLocator(buildId)
  const rootCausesLocator = getProblemRootCausesLocator(buildId)
  const snapshotDependenciesLocator = getSnapshotDependenciesLocator(buildId)
  const buildIds = useAppSelector(
    state => getBuildsData(state, snapshotDependenciesLocator).data,
    shallowEqual,
  )

  const [hideProblemsFromDependencies, toggleHideProblemsFromDependencies] =
    useSyncedBooleanQueryParam({
      param: 'hideProblemsFromDependencies',
      userProperty: UserProperties.HIDE_PROBLEMS_FROM_DEPENDENCIES,
    })

  const endpoint = buildProblemsTreeContextDefaultValue.getEndpoint()

  const {
    countProblems = 0,
    treeProblemsReady,
    refetch: refetchProblemOccurenceTree,
  } = restApi.endpoints.getProblemOccurrenceTree.useQuery(
    {
      endpoint,
      locator: problemsLocator,
      treeLocator: problemsLocator,
    },
    {
      selectFromResult: ({data}) => ({
        countProblems: getRootNode(data?.tree.node)?.count,
        treeProblemsReady: data != null,
      }),
    },
  )
  const fetchProblemOccurrenceTreeHandler = useThrottle(
    refetchProblemOccurenceTree,
    THROTTLER_TIMEOUT,
  )
  const {
    countRootCauses = 0,
    newFailedCountRootCauses = 0,
    hasRootCausesInOtherBuilds,
    treeRootCausesReady,
    refetch: refetchRootCausesTree,
  } = restApi.endpoints.getProblemOccurrenceTree.useQuery(
    {
      endpoint,
      locator: rootCausesLocator,
      treeLocator: rootCausesLocator,
    },
    {
      selectFromResult: ({data}) => {
        const {count, newFailedCount} = getRootNode(data?.tree.node) ?? {}
        return {
          countRootCauses: count,
          newFailedCountRootCauses: newFailedCount,
          hasRootCausesInOtherBuilds: data?.tree.node?.some(
            node =>
              node.type === TreeEntityType.BUILD && toBuildId(node.name) !== toBuildId(buildId),
          ),
          treeRootCausesReady: data != null,
        }
      },
    },
  )
  const fetchRootCausesTreeHandler = useThrottle(refetchRootCausesTree, THROTTLER_TIMEOUT)

  const isReady = treeProblemsReady && treeRootCausesReady
  useSequenceLoaderReadyHandler(isReady)

  const locator =
    countProblems > 0 && hideProblemsFromDependencies ? problemsLocator : rootCausesLocator

  const labelId = 'buildProblemsPreview'
  const renderHeading = useMemo(() => {
    const title = `${countRootCauses} Build ${plur('Problem', countRootCauses)}`
    const countOfNew = newFailedCountRootCauses > 0 ? `, ${newFailedCountRootCauses} new` : ''
    return <span id={labelId}>{`${title}${countOfNew}`}</span>
  }, [countRootCauses, newFailedCountRootCauses])

  const renderHeaderSnippet = useMemo(
    () =>
      countProblems > 0 &&
      hasRootCausesInOtherBuilds && (
        <Checkbox
          containerClassName={styles.hideProblemsFromDependencies}
          checked={hideProblemsFromDependencies}
          onChange={() => toggleHideProblemsFromDependencies(!hideProblemsFromDependencies)}
        >
          {'Hide from dependencies'}
        </Checkbox>
      ),
    [
      countProblems,
      hasRootCausesInOtherBuilds,
      hideProblemsFromDependencies,
      toggleHideProblemsFromDependencies,
    ],
  )

  const renderContent = useMemo(
    () => (
      <Suspense>
        <BuildProblemsTree
          labelId={labelId}
          key={locator}
          locator={locator}
          projectId={projectId}
          buildId={buildId}
        />
      </Suspense>
    ),
    [locator, projectId, buildId],
  )
  useBuildProblemsSubscriptions({buildIds: [buildId], handler: fetchProblemOccurrenceTreeHandler})
  useBuildProblemsSubscriptions({buildIds, handler: fetchRootCausesTreeHandler})

  return isReady && countRootCauses > 0 ? (
    <ContentPanel
      headingClassName={contentPanelStyles.errorHeading}
      panelType={UserProperties.EXPAND_BUILD_PROBLEMS_SECTION}
      heading={renderHeading}
      headerSnippet={renderHeaderSnippet}
      content={renderContent}
    />
  ) : null
}

export default memo<Props>(BuildProblemsPreview)
